import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryClient, withClientId } from "@/lib/queryClient";
import {
  createDeal,
  deleteDeal,
  fetchDeal,
  fetchDeals,
  updateDeal,
  type DealWriteBody,
} from "./deal-api";
import type { Deal } from "@/types/deal";

export const DEALS_KEY = ["deals"] as const;

// Outbox keys (see apps/frontend/AGENTS.md "offline outbox"): paused offline
// mutations replay through the setMutationDefaults registrations below.
export const DEAL_CREATE_KEY = ["outbox", "deals", "create"] as const;
export const DEAL_UPDATE_KEY = ["outbox", "deals", "update"] as const;
export const DEAL_DELETE_KEY = ["outbox", "deals", "delete"] as const;

queryClient.setMutationDefaults(DEAL_CREATE_KEY, { mutationFn: createDeal });
queryClient.setMutationDefaults(DEAL_UPDATE_KEY, {
  mutationFn: (vars: { id: string; body: DealWriteBody }) => updateDeal(vars.id, vars.body),
});
queryClient.setMutationDefaults(DEAL_DELETE_KEY, { mutationFn: deleteDeal });

export const useDeal = (id: string) =>
  useQuery({
    queryKey: ["deals", id],
    queryFn: () => fetchDeal(id),
  });

export const useDeals = (q?: string) => {
  const key: [string, Record<string, unknown>] = ["deals", {}];
  if (q) key[1].q = q;
  return useQuery({
    queryKey: q ? key : DEALS_KEY,
    queryFn: () => fetchDeals(q),
  });
};

export const useCreateDeal = () => {
  const qc = useQueryClient();
  const mutation = useMutation<Deal, Error, DealWriteBody & { id: string }>({
    mutationKey: DEAL_CREATE_KEY,
    mutationFn: createDeal,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deals"] }),
  });
  return {
    ...mutation,
    mutate: (body: DealWriteBody, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate(withClientId(body), options),
    mutateAsync: (body: DealWriteBody, options?: Parameters<typeof mutation.mutateAsync>[1]) =>
      mutation.mutateAsync(withClientId(body), options),
  };
};

export const useDeleteDeal = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: DEAL_DELETE_KEY,
    mutationFn: (id: string) => deleteDeal(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deals"] }),
  });
};

/**
 * useUpdateDeal persists a deal update to the backend. Optimistic UI is now
 * handled by the local-state mirror in `deals-tab.tsx` (per the dnd-kit
 * "Integration with external state" pattern), so this mutation just
 * invalidates on settle. Using `onSettled` (not `onSuccess`) means a failed
 * PUT still triggers a refetch, which lets the local-state mirror revert to
 * server truth via its `useEffect`.
 */
export const useUpdateDeal = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: DEAL_UPDATE_KEY,
    mutationFn: (vars: { id: string; body: DealWriteBody }) =>
      updateDeal(vars.id, vars.body),
    onSettled: (data) => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      if (data) {
        qc.invalidateQueries({ queryKey: ["deals", data.id] });
      }
    },
  });
};
